import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowRight, Sparkles } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LS_KEY = "cardwatch.tour_completed_v1";

const STEPS = [
  {
    id: "link",
    testId: "dashboard-connect-card-button",
    title: "Link your first credit card",
    body: "Tap here to connect a card via Plaid open banking, or add one manually. Everything else on this page comes to life once you do.",
    placement: "bottom",
  },
  {
    id: "cards",
    testId: "linked-cards-card",
    title: "See utilization at a glance",
    body: "Every card you add lands here — with live balance, credit limit, utilization bar and next due date. Due-soon cards get a green ‘I've paid this’ shortcut.",
    placement: "top",
  },
  {
    id: "menu",
    testId: "dashboard-user-menu",
    title: "Set preferences & reminders",
    body: "Open this menu to change your home currency, pick your reminder email address, or turn payment-due emails on and off. Everything lives under Preferences.",
    placement: "bottom",
  },
];

const PADDING = 10; // spotlight halo around target
const TOOLTIP_GAP = 16;
const TOOLTIP_WIDTH = 340;

const useTargetRect = (testId) => {
  const [rect, setRect] = useState(null);

  useLayoutEffect(() => {
    if (!testId) return undefined;
    let raf = 0;
    const measure = () => {
      const el = document.querySelector(`[data-testid="${testId}"]`);
      if (!el) {
        setRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setRect({
        top: r.top,
        left: r.left,
        width: r.width,
        height: r.height,
        bottom: r.bottom,
        right: r.right,
      });
      // Scroll target into view if it's below the fold
      if (r.top < 0 || r.bottom > window.innerHeight) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    };
    const scheduleMeasure = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    };
    scheduleMeasure();
    window.addEventListener("resize", scheduleMeasure);
    window.addEventListener("scroll", scheduleMeasure, true);
    // Re-measure again after scrolling settles (smooth-scroll can take ~400ms)
    const late = window.setTimeout(scheduleMeasure, 450);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(late);
      window.removeEventListener("resize", scheduleMeasure);
      window.removeEventListener("scroll", scheduleMeasure, true);
    };
  }, [testId]);

  return rect;
};

const computeTooltipPosition = (rect, placement) => {
  if (!rect) return { top: 24, left: 24 };
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  let top;
  let left;
  if (placement === "top") {
    top = rect.top - TOOLTIP_GAP - 200; // approx tooltip height buffer
    if (top < 16) top = rect.bottom + TOOLTIP_GAP;
  } else {
    top = rect.bottom + TOOLTIP_GAP;
    if (top + 200 > vh) top = Math.max(16, rect.top - TOOLTIP_GAP - 200);
  }
  left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
  left = Math.min(Math.max(16, left), vw - TOOLTIP_WIDTH - 16);
  return { top, left };
};

const OnboardingTour = ({ user, onComplete }) => {
  const [stepIdx, setStepIdx] = useState(0);
  const [dismissed, setDismissed] = useState(false);

  const step = STEPS[stepIdx];
  const rect = useTargetRect(step?.testId);

  // Persist completion locally + server-side. Silent failure — the localStorage flag
  // is enough to prevent re-appearance on the same device.
  const persistCompletion = async () => {
    try {
      window.localStorage.setItem(LS_KEY, new Date().toISOString());
    } catch { /* localStorage disabled — fine */ }
    try {
      await axios.post(`${API}/auth/complete_tour`, {}, { withCredentials: true });
    } catch { /* ignore — the local flag still hides the tour */ }
    if (onComplete) onComplete();
  };

  const handleSkip = () => {
    setDismissed(true);
    persistCompletion();
  };

  const handleNext = () => {
    if (stepIdx >= STEPS.length - 1) {
      setDismissed(true);
      persistCompletion();
    } else {
      setStepIdx((n) => n + 1);
    }
  };

  // Escape key skips the tour
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") handleSkip();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prevent body scroll while tour is visible (still allow the smooth-scroll
  // triggered by useTargetRect, which happens before this effect runs each step).
  useEffect(() => {
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, []);

  const tooltipPos = useMemo(() => computeTooltipPosition(rect, step?.placement), [rect, step?.placement]);

  if (dismissed || !step) return null;

  // If the target element isn't in the DOM (rare), skip forward gracefully.
  if (!rect) {
    return createPortal(
      <div
        className="fixed inset-0 z-[9999] bg-[#060B19]/70 backdrop-blur-sm flex items-center justify-center"
        data-testid="onboarding-tour-fallback"
      >
        <div className="rounded-2xl bg-[#0B1220] border border-white/10 p-6 max-w-sm text-center">
          <p className="text-sm text-slate-300">Loading tour…</p>
          <button
            onClick={handleSkip}
            className="mt-4 text-xs text-slate-400 hover:text-white underline"
            data-testid="onboarding-tour-fallback-skip"
          >
            Skip tour
          </button>
        </div>
      </div>,
      document.body
    );
  }

  const spotlightStyle = {
    top: rect.top - PADDING,
    left: rect.left - PADDING,
    width: rect.width + PADDING * 2,
    height: rect.height + PADDING * 2,
  };

  return createPortal(
    <div className="fixed inset-0 z-[9999] pointer-events-none" data-testid="onboarding-tour">
      {/* Backdrop rendered as four rectangles around the spotlight for a clean cutout.
          Root and backdrops use pointer-events-none so the tour never blocks recovery
          actions like the Sync cards button. Only the tooltip re-enables pointer events. */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="absolute inset-0 pointer-events-none"
      >
        {/* Top */}
        <div
          className="absolute left-0 right-0 top-0 bg-[#060B19]/80 backdrop-blur-[2px] pointer-events-none"
          style={{ height: Math.max(0, rect.top - PADDING) }}
        />
        {/* Bottom */}
        <div
          className="absolute left-0 right-0 bottom-0 bg-[#060B19]/80 backdrop-blur-[2px] pointer-events-none"
          style={{ top: rect.bottom + PADDING }}
        />
        {/* Left */}
        <div
          className="absolute left-0 bg-[#060B19]/80 backdrop-blur-[2px] pointer-events-none"
          style={{
            top: Math.max(0, rect.top - PADDING),
            width: Math.max(0, rect.left - PADDING),
            height: rect.height + PADDING * 2,
          }}
        />
        {/* Right */}
        <div
          className="absolute right-0 bg-[#060B19]/80 backdrop-blur-[2px] pointer-events-none"
          style={{
            top: Math.max(0, rect.top - PADDING),
            left: rect.right + PADDING,
            height: rect.height + PADDING * 2,
          }}
        />
      </motion.div>

      {/* Spotlight ring */}
      <motion.div
        initial={{ opacity: 0, scale: 0.94 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="absolute rounded-2xl pointer-events-none"
        style={{
          ...spotlightStyle,
          boxShadow: "0 0 0 2px rgba(0, 230, 118, 0.9), 0 0 0 8px rgba(0, 230, 118, 0.18), 0 0 60px rgba(0, 230, 118, 0.35)",
        }}
        data-testid="onboarding-tour-spotlight"
      />

      {/* Tooltip card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step.id}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.22 }}
          style={{
            top: tooltipPos.top,
            left: tooltipPos.left,
            width: TOOLTIP_WIDTH,
          }}
          className="absolute rounded-2xl bg-[#0B1220]/95 backdrop-blur-xl border border-white/10 shadow-[0_30px_80px_-20px_rgba(0,0,0,0.8)] p-5 pointer-events-auto"
          data-testid={`onboarding-tour-tooltip-${step.id}`}
        >
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-2">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-[#00E676]/15 text-[#00E676]">
                <Sparkles size={12} />
              </span>
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#00E676] font-bold">
                Step {stepIdx + 1} of {STEPS.length}
              </span>
            </div>
            <button
              onClick={handleSkip}
              className="text-slate-500 hover:text-white transition-colors"
              data-testid="onboarding-tour-close"
              aria-label="Close tour"
            >
              <X size={16} />
            </button>
          </div>

          <h3
            className="text-white text-base font-medium leading-snug mb-2"
            style={{ fontFamily: "'Outfit', sans-serif" }}
            data-testid={`onboarding-tour-title-${step.id}`}
          >
            {step.title}
          </h3>
          <p className="text-slate-300 text-sm leading-relaxed">{step.body}</p>

          <div className="mt-5 flex items-center justify-between">
            <div className="flex items-center gap-1.5" data-testid="onboarding-tour-dots">
              {STEPS.map((_, i) => (
                <span
                  key={i}
                  className={`h-1.5 rounded-full transition-all ${
                    i === stepIdx ? "w-6 bg-[#00E676]" : "w-1.5 bg-white/20"
                  }`}
                />
              ))}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleSkip}
                className="text-xs text-slate-400 hover:text-white transition-colors"
                data-testid="onboarding-tour-skip"
              >
                Skip tour
              </button>
              <button
                onClick={handleNext}
                className="inline-flex items-center gap-1.5 rounded-full bg-[#00E676] hover:bg-[#00C863] text-[#060B19] font-semibold px-4 py-2 text-xs transition-colors"
                data-testid="onboarding-tour-next"
              >
                {stepIdx === STEPS.length - 1 ? "Got it" : "Next"}
                <ArrowRight size={12} />
              </button>
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>,
    document.body
  );
};

export const useShouldShowTour = (user, demoMode) => {
  const [ready, setReady] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    if (demoMode) {
      setShouldShow(false);
      setReady(true);
      return;
    }
    if (!user) {
      setShouldShow(false);
      setReady(true);
      return;
    }
    let localSeen = false;
    try {
      localSeen = Boolean(window.localStorage.getItem(LS_KEY));
    } catch { /* ignore */ }
    const serverSeen = Boolean(user.tour_completed_at);
    setShouldShow(!localSeen && !serverSeen);
    setReady(true);
  }, [user, demoMode]);

  return { ready, shouldShow };
};

export default OnboardingTour;
